import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { formatAndSanitizeHTML } from 'utils/misc'
import { ISO8601_DATETIME_FORMATS } from 'utils/constants'
import { DatePicker, Input } from 'components/UIElements'
import SurveyQuestion from '../SurveyQuestion'
import MakeScrollable from '../MakeScrollable'
import QuestionFooter from '../QuestionFooter'
import { allowEnterPress } from '../utils'
import {
  checkLogicAndComplete,
  getShouldRenderNextQuestion,
} from '../../utils/instrumentUtils'

const getLocalDateFormat = (selectedLocale) => {
  const localeKey = selectedLocale || 'en'
  return moment().locale(localeKey).localeData()._longDateFormat.L
}

const buildDateAnswer = ({ label, id, selectedLocale, date }) => {
  if (!date) return []
  const momentDate = moment.isMoment(date)
    ? date
    : moment(date, moment.HTML5_FMT.DATE)

  if (!momentDate?.isValid?.() || !momentDate.isValid()) return []

  const localDateFormat = getLocalDateFormat(selectedLocale)
  return [
    {
      label,
      value: `${momentDate.format(localDateFormat)}, ${localDateFormat}, ${momentDate.format(
        ISO8601_DATETIME_FORMATS.date,
      )}`,
      question: id,
    },
  ]
}

const completeDateQuestion = (props, date) => {
  const {
    question,
    id,
    completeQuestion,
    nextQ,
    webSurvey,
    selectedLocale,
    submissionSurvey,
    nextQId,
    reviewFunctionalitySupported,
    isConsent = false,
    isLastQuestion = false,
    isSibling = false,
  } = props
  const { label, type, attributes } = question
  const { required } = attributes

  const answer = buildDateAnswer({ label, id, selectedLocale, date })
  const submission = {
    id,
    label,
    name: id,
    type,
    required,
    state: date ? 'ANSWERED' : 'SKIPPED',
    answer
  }
  const currentAnswer = { [id]: answer }

  const shouldRenderNextQ = getShouldRenderNextQuestion({
    isConsent,
    isLastQuestion,
    isSibling,
  })

  checkLogicAndComplete(
    webSurvey,
    id,
    submissionSurvey,
    currentAnswer,
    nextQId,
    nextQ,
    completeQuestion,
    submission,
    reviewFunctionalitySupported,
    shouldRenderNextQ,
  )
}

function DateType(props) {
  const [date, setDate] = useState('')
  const {
    className,
    currentSurvey,
    id,
    isConsent,
    isLastQuestion,
    question,
    renderBackButton,
    selectedLocale,
    setAllRequiredAnswered,
    strings,
    storedAnswer,
    forwardedRef,
    submissionLoading,
    submissionSurvey,
    webSurvey,
    isSibling = false,
  } = props
  const { label, attributes } = question
  const { required } = attributes
  const _className = className ? ` ${className}` : ''

  useEffect(() => {
    let isMounted = true

    if (storedAnswer?.[0]?.value) {
      const value = storedAnswer?.[0]?.value
      const parts = String(value)
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
      const isoDate = parts[2] // stored as YYYY-MM-DD
      const parsed = isoDate
        ? moment(isoDate, ISO8601_DATETIME_FORMATS.date, true)
        : moment.invalid()
      if (isMounted) {
        if (parsed.isValid()) onInput(parsed)
        // Complete immediately with the stored value
        if (!props.suppressAutoAdvance) {
          if (parsed.isValid()) completeDateQuestion(props, parsed)
        }
      }
    }

    return () => {
      isMounted = false
    }
  }, [])

  const onInput = (value) => {
    setAllRequiredAnswered(false, id)
    setDate(value)
    // Autosave into Redux so sibling pages see answers and can enable Next
    // (per-question footer is hidden when isSibling).
    try {
      const { question, webSurvey, completeQuestion } = props
      const { label, type, attributes } = question
      const { required } = attributes
      const answer = buildDateAnswer({ label, id, selectedLocale, date: value })
      const submission = {
        id,
        label,
        name: id,
        type,
        required,
        state: value ? 'ANSWERED' : 'SKIPPED',
        answer,
      }
      if (typeof completeQuestion === 'function' && webSurvey) {
        completeQuestion(submission, webSurvey.order?.[0])
      }
    } catch (e) {
      console.warn('DateType autosave failed', { error: e })
    }
  }

  return (
    <SurveyQuestion id={id}>
      <MakeScrollable>
        <div ref={forwardedRef} className={`survey-question date${_className}`}>
          <p className='question-label'>
            <span
              dangerouslySetInnerHTML={{
                __html: formatAndSanitizeHTML(label)
              }}
            />
          </p>
          <DatePicker
            className='date-picker desktop-only'
            onDayChange={(val) => onInput(val || '')} // This prevents the uncontrolled input error if the val becomes undefined
            onEnterPress={() => {
              if (allowEnterPress(required, date)) {
                completeDateQuestion(props, date)
              }
            }}
            date={date}
            selectedLocale={selectedLocale}
          />
          <Input
            className='mobile-only'
            type='date'
            onChange={(val) => onInput(moment(val))}
            value={date && date.format('YYYY-MM-DD')}
          />
          {!isSibling && (
            <QuestionFooter
              completed={!!date}
              id={id}
              isConsent={isConsent}
              isLastQuestion={isLastQuestion}
              loading={submissionLoading}
              onComplete={() => completeDateQuestion(props, date)}
              question={question}
              renderBackButton={renderBackButton}
              strings={strings}
              submissionSurvey={submissionSurvey}
              webSurvey={webSurvey}
            />
          )}
        </div>
      </MakeScrollable>
    </SurveyQuestion>
  )
}

const ForwardedDateType = React.forwardRef((props, ref) => (
  <DateType {...props} forwardedRef={ref} />
))

export default ForwardedDateType

DateType.propTypes = {
  className: PropTypes.string,
  currentSurvey: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.string,
  question: PropTypes.object,
  renderBackButton: PropTypes.func,
  selectedLocale: PropTypes.string,
  setAllRequiredAnswered: PropTypes.func,
  strings: PropTypes.objectOf(PropTypes.string),
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
  ])
}
